import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import Seo from "../../components/feature/Seo";
import ComicCard from "../../components/feature/ComicCard";
import { comics, formatViews } from "../../mocks/comics";

export default function Detail() {
  const { id } = useParams();
  const comic = useMemo(() => comics.find((c) => c.id === id), [id]);
  const related = useMemo(
    () => (comic ? comics.filter((c) => c.category === comic.category && c.id !== comic.id).slice(0, 4) : []),
    [comic]
  );

  if (!comic) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center text-center px-4 py-24">
          <i className="ri-file-search-line text-6xl text-foreground-300"></i>
          <h1 className="mt-4 font-heading text-2xl text-foreground-800">未找到该漫画</h1>
          <p className="mt-2 text-foreground-600 text-sm">可能已被下架或地址有误</p>
          <Link to="/fenlei" className="mt-6 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600">返回漫画库</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const chapters = Array.from({ length: Math.min(comic.chapters, 30) }, (_, i) => comic.chapters - i);
  const chapterBase = comic.status === "已完结" ? "全书" : "第";

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />
      <Seo
        title={`${comic.title} - ${comic.category}漫画 | 策驰漫画网`}
        description={`${comic.title}是${comic.region}${comic.category}漫画，作者${comic.author}，${comic.status}，评分${comic.score.toFixed(1)}。${comic.description}`}
        canonical={`https://artbinge.github.io/manhua/${comic.id}`}
      />

      {/* 顶部横幅 */}
      <section className="pt-16 md:pt-[72px] bg-primary-600 relative overflow-hidden">
        <img src={comic.cover} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-15 blur-sm" />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/70 to-primary-700/40"></div>
        <div className="relative z-10 mx-auto max-w-7xl w-full px-4 md:px-6 py-8 md:py-10">
          <nav aria-label="面包屑" className="text-primary-100 text-xs md:text-sm mb-4">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li><Link to="/" className="hover:text-background-50">首页</Link></li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
              <li><Link to="/fenlei" className="hover:text-background-50">漫画库</Link></li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-background-50">{comic.title}</li>
            </ol>
          </nav>
        </div>
      </section>

      {/* 主体 */}
      <section className="mx-auto max-w-7xl w-full px-4 md:px-6 py-8 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 左侧信息 */}
          <div className="lg:col-span-2">
            <div className="flex flex-col sm:flex-row gap-6">
              <div className="w-full sm:w-52 shrink-0">
                <div className="relative rounded-xl overflow-hidden border border-background-200">
                  <img src={comic.cover} alt={`${comic.title}漫画封面`} title={`${comic.title} - ${comic.category}漫画`} className="w-full aspect-[3/4] object-cover object-top" />
                  <span className={`absolute top-2 left-2 px-2 py-0.5 rounded-md text-[11px] font-medium ${comic.status === "已完结" ? "bg-accent-500 text-foreground-950" : "bg-primary-500 text-background-50"}`}>{comic.status}</span>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-950">{comic.title}</h1>
                <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-foreground-600">
                  <span className="flex items-center gap-1.5"><i className="ri-user-3-line text-primary-600"></i>作者：{comic.author}</span>
                  <span className="flex items-center gap-1.5"><i className="ri-book-2-line text-secondary-600"></i>{comic.chapters}话</span>
                  <span className="flex items-center gap-1.5"><i className="ri-eye-line text-foreground-500"></i>{formatViews(comic.views)}人气</span>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <span className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium">{comic.category}</span>
                  <span className="px-3 py-1.5 rounded-full bg-background-100 border border-background-200 text-foreground-600 text-xs">{comic.region}</span>
                  <span className="flex items-center gap-1 text-accent-700 text-lg font-bold"><i className="ri-star-fill text-accent-500"></i>{comic.score.toFixed(1)}</span>
                  <span className="text-xs text-foreground-500">{comic.year}年</span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {comic.tags.map((t) => (
                    <Link key={t} to={`/fenlei?tag=${encodeURIComponent(t)}`} className="px-3 py-1.5 rounded-full bg-background-100 border border-background-200 text-xs text-foreground-600 hover:bg-primary-100 hover:text-primary-700 hover:border-primary-200 transition-colors">#{t}</Link>
                  ))}
                </div>
                <div className="mt-5 flex flex-wrap gap-3">
                  <button type="button" className="whitespace-nowrap px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors inline-flex items-center gap-2"><i className="ri-play-fill"></i>开始阅读</button>
                  <button type="button" className="whitespace-nowrap px-6 py-3 rounded-full bg-accent-500 text-foreground-950 text-sm font-semibold hover:bg-accent-600 transition-colors inline-flex items-center gap-2"><i className="ri-star-line"></i>收藏</button>
                </div>
              </div>
            </div>

            {/* 简介 */}
            <article className="mt-8 bg-background-100 border border-background-200/70 rounded-2xl p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-bold text-foreground-950 mb-3"><i className="ri-file-list-3-line text-primary-600"></i>漫画简介</h2>
              <p className="text-sm text-foreground-700 leading-relaxed">{comic.description}</p>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                《{comic.title}》是{comic.region}一部{comic.category}题材的优质漫画，由{comic.author}创作。作品在策驰漫画网人气排行榜中持续上榜，凭借扎实的剧情与精美的画风获得读者广泛好评。目前{comic.status === "已完结" ? "已完结，可以一次看过瘾" : `正在持续更新，最新章节为「${comic.lastChapter}」`}。
              </p>
            </article>

            {/* 章节 */}
            <section className="mt-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="flex items-center gap-2 font-bold text-foreground-950"><i className="ri-list-unordered text-secondary-600"></i>章节目录</h2>
                <span className="text-xs text-foreground-500">共 {comic.chapters} 话</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                {chapters.map((num) => (
                  <button key={num} type="button" className="text-left px-4 py-3 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-700 hover:bg-primary-50 hover:border-primary-300 hover:text-primary-700 transition-colors truncate">
                    {comic.status === "已完结" ? `${chapterBase}${comic.chapters === num ? "（终章）" : `第${num}话`}` : `${chapterBase}${num}话`}
                  </button>
                ))}
              </div>
              <button type="button" className="mt-4 w-full py-3 rounded-xl bg-background-100 border border-dashed border-background-300 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors">
                查看全部 {comic.chapters} 话
              </button>
            </section>
          </div>

          {/* 右侧 */}
          <aside className="space-y-6">
            <div className="bg-background-100 border border-background-200/70 rounded-2xl p-5">
              <h3 className="font-bold text-foreground-950 flex items-center gap-2 mb-4"><i className="ri-fire-line text-accent-500"></i>同类推荐</h3>
              <div className="space-y-3">
                {related.map((c) => (
                  <Link key={c.id} to={`/manhua/${c.id}`} className="flex items-center gap-3 group">
                    <div className="w-11 h-14 shrink-0 rounded-md overflow-hidden">
                      <img src={c.cover} alt={`${c.title}封面`} className="w-full h-full object-cover object-top" loading="lazy" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-semibold text-foreground-950 truncate group-hover:text-primary-600">{c.title}</h4>
                      <p className="text-xs text-foreground-600 truncate mt-0.5">{c.category} · {c.score.toFixed(1)}分</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* 相关作品 */}
      {related.length > 0 && (
        <section className="mx-auto max-w-7xl w-full px-4 md:px-6 pb-12 md:pb-16">
          <h2 className="flex items-center gap-2 font-heading text-xl md:text-2xl text-primary-700 mb-5"><i className="ri-bookmark-line text-accent-500"></i>你可能会喜欢</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {related.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        </section>
      )}

      <Footer />

      {/* Article 结构化数据 */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": comic.title,
        "author": { "@type": "Person", "name": comic.author },
        "image": comic.cover,
        "description": comic.description,
        "inLanguage": "zh-CN",
      }) }} />
    </div>
  );
}